import { useState, useEffect } from 'react'
import { Plus, X, ChevronDown } from 'lucide-react'
import { Autocomplete, AutocompleteItem } from '@heroui/react'
import { CollapsibleSection } from '../shared/CollapsibleSection'
import { MultiSelect } from '../shared/MultiSelect'
import { funnelApi } from '../../api'
import { COUNTRIES } from '../../lib/constants'
import type { Funnel, Product } from '../../types'
import { inputClass, selectClass, labelClass } from '../../styles/class-utils'

interface FunnelSettingsProps {
  funnel: Funnel
  onChange: (updates: Partial<Funnel>) => void
}

const FUNNEL_TYPE_OPTIONS = [
  { value: 'post_purchase', label: 'Post Purchase' },
  { value: 'pre_purchase', label: 'Pre Purchase' },
  { value: 'landing_page', label: 'Landing Page' },
]

const TRAFFIC_TYPE_OPTIONS = [
  { value: 'organic', label: 'Organic' },
  { value: 'paid', label: 'Paid' },
  { value: 'email', label: 'Email' },
  { value: 'social', label: 'Social' },
  { value: 'all', label: 'All Traffic' },
]

export function FunnelSettings({ funnel, onChange }: FunnelSettingsProps) {
  const [products, setProducts] = useState<Product[]>([])

  useEffect(() => {
    funnelApi.listProducts()
      .then((res) => setProducts(res.data))
      .catch(() => {})
  }, [])

  const countryOptions = COUNTRIES.map((c) => ({
    value: c.code,
    label: c.name,
    icon: c.flag,
  }))

  const urls = funnel.landingPageUrls || []

  const handleAddUrl = () => {
    onChange({ landingPageUrls: [...urls, ''] })
  }

  const handleRemoveUrl = (index: number) => {
    onChange({ landingPageUrls: urls.filter((_, i) => i !== index) })
  }

  const handleUrlChange = (index: number, value: string) => {
    const updated = [...urls]
    updated[index] = value
    onChange({ landingPageUrls: updated })
  }

  return (
    <CollapsibleSection title="Funnel Settings">
      <div className="space-y-5 pt-3">
        <div>
          <label className={labelClass}>
            Funnel Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            className={inputClass}
            placeholder="Enter funnel name"
            value={funnel.name}
            onChange={(e) => onChange({ name: e.target.value })}
            required
          />
        </div>

        <div>
          <label className={labelClass}>Description</label>
          <textarea
            className={`${inputClass} resize-none`}
            placeholder="Describe the funnel purpose and strategy"
            value={funnel.description || ''}
            onChange={(e) => onChange({ description: e.target.value })}
            rows={2}
          />
        </div>

        <div>
          <label className={labelClass}>Product</label>
          <Autocomplete
            placeholder="Search or enter product ID"
            defaultInputValue={
              products.find((p) => p.id === funnel.productId)?.name || funnel.productId || ''
            }
            selectedKey={funnel.productId || null}
            onSelectionChange={(key) => {
              if (key !== null) onChange({ productId: String(key) })
            }}
            onInputChange={(value) => {
              const match = products.find((p) => p.name === value)
              if (!match && value) onChange({ productId: value })
            }}
            variant="bordered"
            allowsCustomValue
            size="sm"
          >
            {products.map((product) => (
              <AutocompleteItem key={product.id} textValue={product.name}>
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{product.name}</span>
                  <span className="text-xs text-default-400">{product.sku}</span>
                </div>
              </AutocompleteItem>
            ))}
          </Autocomplete>
        </div>

        <div>
          <label className={labelClass}>Funnel Type</label>
          <div className="relative">
            <select
              className={selectClass}
              value={funnel.funnelType || ''}
              onChange={(e) => onChange({ funnelType: e.target.value as Funnel['funnelType'] })}
            >
              <option value="">Select funnel type</option>
              {FUNNEL_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fn-text-muted pointer-events-none" />
          </div>
        </div>

        <div>
          <label className={labelClass}>Traffic Type</label>
          <div className="relative">
            <select
              className={selectClass}
              value={funnel.trafficType || ''}
              onChange={(e) => onChange({ trafficType: e.target.value as Funnel['trafficType'] })}
            >
              <option value="">Select traffic type</option>
              {TRAFFIC_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fn-text-muted pointer-events-none" />
          </div>
        </div>

        <MultiSelect
          label="Countries"
          options={countryOptions}
          selected={funnel.countries || []}
          onChange={(selected) => onChange({ countries: selected })}
          placeholder="Select countries"
        />

        <div>
          <label className={labelClass}>Landing Page URLs</label>
          <div className="space-y-2 mt-0.5">
            {urls.map((url, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="url"
                  className={`${inputClass} flex-1`}
                  placeholder="https://example.com/product-page"
                  value={url}
                  onChange={(e) => handleUrlChange(index, e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => handleRemoveUrl(index)}
                  className="p-1.5 rounded-lg transition-colors hover:bg-tf-danger-light text-fn-text-muted hover:text-red-500"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddUrl}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-dashed border-fn-border text-fn-text-muted transition-colors hover:bg-fn-hover hover:text-fn-text-sub"
            >
              <Plus className="w-4 h-4" />
              Add URL
            </button>
            {urls.length === 0 && (
              <p className="text-xs text-fn-text-muted">
                Add at least one landing page URL for this funnel
              </p>
            )}
          </div>
        </div>

        <div>
          <label className={labelClass}>Priority</label>
          <input
            type="number"
            className={inputClass}
            placeholder="Enter priority (higher = first)"
            value={String(funnel.priority)}
            onChange={(e) => onChange({ priority: parseInt(e.target.value, 10) || 0 })}
          />
          <p className="mt-1 text-xs text-fn-text-muted">Lower number = higher priority</p>
        </div>

        <div className="flex items-center gap-3 pt-1">
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={funnel.isActive}
              onChange={(e) => onChange({ isActive: e.target.checked })}
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-tf-bg-hover peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-tf-bg-card after:border-tf-border after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary" />
            <span className="ms-3 text-sm font-medium text-fn-text">Active</span>
          </label>
        </div>
      </div>
    </CollapsibleSection>
  )
}
